import type { BlogPost } from '../types';

/**
 * The /blog page's live list: every published post as a card, or a note for visitors when there
 * are none.
 *
 * One component for BOTH hosts — the tenant route at the page's `automatic_content` block (or under
 * its built-in header), and the Studio in the same spot from the same `content.blog_posts`, so the
 * editor shows the posts visitors see rather than a placeholder. Framework-agnostic (plain <a>).
 * The `blog-*` class hooks take the site's Blog look (`data-tmpl-blog` + cardStyles.css).
 */
export type BlogPostCardData = BlogPost & { id?: string | null };

export interface BlogPostCardListProps {
  /** Published posts only, newest first — the host filters. */
  posts: BlogPostCardData[];
  primaryColor: string;
  surfaceColor: string;
  /** The byline when a post names no author. */
  companyName: string;
}

export function BlogPostCardList({ posts, primaryColor, surfaceColor, companyName }: BlogPostCardListProps) {
  return (
    <section className="auto-list-band bg-white py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {posts.length === 0 ? (
          <div className="rounded-3xl border border-gray-100 bg-gray-50 px-8 py-20 text-center shadow-sm">
            <h2 className="text-3xl font-heading font-bold text-gray-900">No posts yet</h2>
            <p className="mx-auto mt-3 max-w-2xl text-lg text-gray-500">
              Check back soon for market updates and real estate tips.
            </p>
            <div className="mt-8">
              <a
                href="/"
                className="inline-flex rounded-lg px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                Back to Home
              </a>
            </div>
          </div>
        ) : (
          <div className="blog-grid auto-list-grid grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => (
              <article
                key={post.id || post.slug}
                className="blog-card auto-list-card blk-card overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-transform hover:-translate-y-1"
              >
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.cover_image_alt || post.title}
                    className="blog-card-cover h-56 w-full object-cover"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="blog-card-cover flex h-56 items-center justify-center" style={{ backgroundColor: surfaceColor }}>
                    <span className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: primaryColor }}>
                      Blog Post
                    </span>
                  </div>
                )}
                <div className="blog-card-body p-7">
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    <span>{post.author || companyName}</span>
                    <span>•</span>
                    <span>{post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Published'}</span>
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-gray-900">
                    <a href={`/blog/${post.slug}`} className="hover:underline">
                      {post.title}
                    </a>
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-gray-600">
                    {post.excerpt || 'Read the full post for more insights.'}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(post.tags || []).slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border px-3 py-1 text-[11px] font-semibold"
                        style={{ borderColor: `${primaryColor}33`, color: primaryColor, backgroundColor: `${primaryColor}10` }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6">
                    <a
                      href={`/blog/${post.slug}`}
                      className="blk-link inline-flex text-sm font-semibold hover:opacity-80"
                      style={{ color: primaryColor }}
                    >
                      Read post →
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
